import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { User } from '@supabase/supabase-js';
import PersonIcon from '@mui/icons-material/Person';
import BarChartIcon from '@mui/icons-material/BarChart';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import DownloadIcon from '@mui/icons-material/Download';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { UserProfile } from '../../../lib/auth-state-machine';
import { BRAND_COLORS, alpha } from '../../../styles/theme';
import { useTranslation } from '../../../i18n';
import { UserName, UserRole } from '../Dashboard.styles';
import { useAnalyticsAccessQuery, useAgentsAccessQuery } from '../../../hooks/access.queries';
import { useDashboardManufacturingAccessQuery } from './dashboard.queries';

interface UserMenuDropdownProps {
  user: User | null;
  profile: UserProfile | null;
  onSignOut: () => Promise<void>;
  isLoggingOut?: boolean;
}

export const UserMenuDropdown: React.FC<UserMenuDropdownProps> = ({
  user,
  profile,
  onSignOut,
  isLoggingOut = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Silent access probes (STD-015): a denied/failed check just hides the menu
  // item, so these opt out of the global error toast. Default to false until
  // resolved, matching the previous useState(false) + .catch(false) behavior.
  const { data: hasAnalyticsAccess = false } = useAnalyticsAccessQuery();
  const { data: hasAgentsAccess = false } = useAgentsAccessQuery();
  const { data: hasManufacturingAccess = false } = useDashboardManufacturingAccessQuery();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMyProfile = () => {
    handleClose();
    if (user?.id) {
      navigate(`/user/${user.id}`);
    }
  };

  const handleSignOut = () => {
    handleClose();
    onSignOut();
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <Button
        onClick={handleClick}
        endIcon={<ExpandMoreIcon />}
        sx={{
          textTransform: 'none',
          color: BRAND_COLORS.forestGreen.main,
          padding: '4px 12px',
          borderRadius: 1,
          '&:hover': {
            backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.08),
          },
        }}
      >
        <Box sx={{ textAlign: 'right' }}>
          <UserName sx={{ mb: 0 }}>{user.email || 'User'}</UserName>
          <UserRole sx={{ mt: 0 }}>
            {profile?.organization?.roles?.map((r) => r.toUpperCase()).join(', ') || 'USER'}
          </UserRole>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 180,
            mt: 1,
          },
        }}
      >
        <MenuItem onClick={handleMyProfile}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('profile.myProfile')}</ListItemText>
        </MenuItem>

        {hasAnalyticsAccess && (
          <MenuItem
            onClick={() => {
              handleClose();
              navigate('/website-analytics');
            }}
          >
            <ListItemIcon>
              <BarChartIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Website Analytics</ListItemText>
          </MenuItem>
        )}

        {hasAgentsAccess && (
          <MenuItem
            onClick={() => {
              handleClose();
              navigate('/agents');
            }}
          >
            <ListItemIcon>
              <SmartToyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Agents</ListItemText>
          </MenuItem>
        )}

        {hasManufacturingAccess && (
          <MenuItem
            onClick={() => {
              handleClose();
              navigate('/manufacturing');
            }}
          >
            <ListItemIcon>
              <PrecisionManufacturingIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Manufacturing</ListItemText>
          </MenuItem>
        )}

        <MenuItem
          data-testid="menu-downloads"
          onClick={() => {
            handleClose();
            navigate('/downloads');
          }}
        >
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Downloads</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleSignOut} disabled={isLoggingOut}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color={isLoggingOut ? 'disabled' : 'error'} />
          </ListItemIcon>
          <ListItemText sx={{ color: isLoggingOut ? 'text.disabled' : 'error.main' }}>
            {isLoggingOut ? t('auth.signingOut') : t('auth.signOut')}
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserMenuDropdown;
